import { shallowEqual } from 'react-redux';

import { Alert, IconBulb, List, Spacer } from '@zillow/constellation';
import { Text, TextButton } from '@zillow/constellation-10';

import { useSelector } from '../../selectors/useSelector';

import {
  RECS,
  RECS_ARRAY_ORDER,
  RecsLearnMoreConstants,
  REVIEW_PAGE_SECTION_RECS,
} from '../../constants/RecommendationsConstants';

import Add3DToursCSEvents from '../../model/ClickstreamEvent/Add3DToursCSEvents';

import { StyledListItem } from '../../StyledComponents/StyledListItem';

type RecommendationsAlertProps = {
  sectionName: string;
  onRecsClick: (recKey: keyof typeof RecsLearnMoreConstants) => void;
};

const RecommendationsAlert = ({ sectionName, onRecsClick }: RecommendationsAlertProps) => {
  const { currentProperty, recommendations, user } = useSelector(
    (state) => ({
      currentProperty: state?.properties?.currentProperty,
      recommendations: state?.properties?.recommendations,
      user: state?.user,
    }),
    shallowEqual,
  );

  if (!recommendations?.[sectionName]) {
    return null;
  }

  const unsortedRecKeys = Object.keys(recommendations[sectionName]);

  if (unsortedRecKeys.length === 0) {
    return null;
  }

  const enforceSortedKeys = RECS_ARRAY_ORDER.hasOwnProperty(sectionName);
  const sortedRecKeys: Array<keyof typeof RecsLearnMoreConstants> = [];

  if (enforceSortedKeys) {
    unsortedRecKeys.forEach((recKey) => (sortedRecKeys[RECS_ARRAY_ORDER[sectionName][recKey]] = recKey));
  }

  let keysToIterateOver = enforceSortedKeys ? sortedRecKeys.filter(Boolean) : unsortedRecKeys;
  // TODO: find out if we can delete this - did API stop returning the deprecated fields?
  if (sectionName === REVIEW_PAGE_SECTION_RECS.POLICIES_AND_TERMS) {
    // filter out the following: administrativeFee, applicationFee, securityDeposit
    keysToIterateOver = keysToIterateOver.filter(
      (key) => key !== RECS.ADMINISTRATIVE_FEE && key !== RECS.APPLICATION_FEE && key !== RECS.SECURITY_DEPOSIT,
    );
  }

  return (
    <Spacer marginBottom="sm">
      <Alert
        icon={<IconBulb fontColor="brand" />}
        appearance="info"
        body={
          keysToIterateOver.length === 1 ? (
            <>
              <Text textStyle="body-sm">{RecsLearnMoreConstants[keysToIterateOver[0]].heading}</Text>

              <TextButton
                textStyle="body-sm"
                css={{ marginLeft: '200' }}
                data-eventid={
                  keysToIterateOver[0] === RECS.VIRTUAL_TOUR ? Add3DToursCSEvents.LearnMore.data_event_id : undefined
                }
                onClick={() => {
                  if (keysToIterateOver[0] === RECS.VIRTUAL_TOUR) {
                    Add3DToursCSEvents.LearnMore({ currentProperty, user });
                  }
                  onRecsClick(keysToIterateOver[0]);
                }}
              >
                Learn more
              </TextButton>
            </>
          ) : (
            <List>
              {keysToIterateOver.map((recKey, idx) => (
                <StyledListItem key={idx}>
                  <Text textStyle="body-sm">{RecsLearnMoreConstants[recKey].heading}</Text>

                  <TextButton
                    textStyle="body-sm"
                    css={{ marginLeft: '200' }}
                    data-eventid={recKey === RECS.VIRTUAL_TOUR ? Add3DToursCSEvents.LearnMore.data_event_id : undefined}
                    onClick={() => {
                      if (recKey === RECS.VIRTUAL_TOUR) {
                        Add3DToursCSEvents.LearnMore({ currentProperty, user });
                      }
                      onRecsClick(recKey);
                    }}
                  >
                    Learn more
                  </TextButton>
                </StyledListItem>
              ))}
            </List>
          )
        }
      />
    </Spacer>
  );
};

export default RecommendationsAlert;
